import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { GuestDiscountSummaryCard } from '../components/GuestDiscountSummaryCard';
import { ProgressCircle } from '../components/ProgressCircle';
import { GuestBottomNav } from '../components/GuestBottomNav';
import { GuestTipReminderCard } from '../components/GuestTipReminderCard';
import { VenueRatingReminderCard } from '../components/VenueRatingReminderCard';
import NotificationBellButton from '../components/NotificationBellButton';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import WelcomeTourSheet from '../components/WelcomeTourSheet';
import PageInfoButton from '../components/PageInfoButton';
import { guestWelcomeTourSlides } from '../components/welcomeTourSlides';
import { hasSeenWelcomeTour, markWelcomeTourSeen } from '../utils/welcomeTourState';
import { IOSInstallBanner } from '../components/IOSInstallBanner';
import { clearPendingPartnerIntent } from '../utils/partnerIntent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const defaultProgress = { level: 1, max_level: 8, discount: 8, total_spent: 0, total_saved: 0, recent_savings: [], next_threshold: 25, current_threshold: 0, progress_percent: 0 };

export default function GuestHomePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [progress, setProgress] = useState(null);
  const [tipReminder, setTipReminder] = useState(null);
  const [dismissingTipReminder, setDismissingTipReminder] = useState(false);
  const [ratingPrompt, setRatingPrompt] = useState(null);
  const [skippingRating, setSkippingRating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tourOpen, setTourOpen] = useState(false);

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // A guest who lands here has completed the fresh-signup guest flow —
  // wipe any lingering partner intent so a later cold-launch stops
  // trying to re-route into partner onboarding.
  useEffect(() => { clearPendingPartnerIntent(); }, []);

  const fetchProgress = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/guest/progress`, { headers });
      setProgress(response.data);
    } catch {
      setProgress(defaultProgress);
    } finally {
      setLoading(false);
    }
  }, [headers, token]);

  const fetchTipReminder = useCallback(async () => {
    if (!token) {
      setTipReminder(null);
      return;
    }

    try {
      const response = await axios.get(`${API}/post-stay-tips/pending-home-card`, { headers });
      setTipReminder(response.data?.prompt || null);
    } catch {
      setTipReminder(null);
    }
  }, [headers, token]);

  const fetchRatingPrompt = useCallback(async () => {
    if (!token) {
      setRatingPrompt(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/venue-reviews/home-pending`, { headers });
      setRatingPrompt(response.data?.prompt || null);
    } catch {
      setRatingPrompt(null);
    }
  }, [headers, token]);

  const handleSkipRating = useCallback(async () => {
    if (!ratingPrompt || skippingRating) return;
    setSkippingRating(true);
    try {
      await axios.post(`${API}/venue-reviews/skip-home/${ratingPrompt.transaction_id}`, {}, { headers });
      await fetchRatingPrompt();
    } catch {
      toast.error('Could not skip this rating right now.');
    } finally {
      setSkippingRating(false);
    }
  }, [headers, fetchRatingPrompt, ratingPrompt, skippingRating]);

  const handleOpenRating = useCallback(() => {
    if (!ratingPrompt) return;
    navigate(`/guest/rate/${ratingPrompt.transaction_id}`);
  }, [navigate, ratingPrompt]);

  const checkPending = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      const response = await axios.get(`${API}/transactions/pending`, { headers });
      if (response.data.pending) {
        navigate(`/guest/payment/${response.data.transaction.id}`);
      }
    } catch {
      // ignore pending-payment polling errors
    }
  }, [headers, navigate, token]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    if (!hasSeenWelcomeTour('guest')) {
      setTourOpen(true);
    }
  }, []);

  const handleCloseTour = useCallback(() => {
    setTourOpen(false);
    markWelcomeTourSeen('guest');
  }, []);

  useEffect(() => {
    fetchProgress();
    fetchTipReminder();
    fetchRatingPrompt();
    checkPending();

    const interval = window.setInterval(() => {
      checkPending();
      fetchTipReminder();
      fetchRatingPrompt();
    }, 12000);

    return () => window.clearInterval(interval);
  }, [checkPending, fetchProgress, fetchTipReminder, fetchRatingPrompt]);

  const handleOpenTipReminder = useCallback(() => {
    if (!tipReminder?.guest_pass_id) {
      return;
    }

    navigate(tipReminder.action_path || `/guest/tips/${tipReminder.guest_pass_id}`);
  }, [navigate, tipReminder]);

  const handleDismissTipReminder = useCallback(async () => {
    if (!tipReminder?.guest_pass_id) {
      return;
    }

    setDismissingTipReminder(true);
    try {
      await axios.post(`${API}/post-stay-tips/guest-pass/${tipReminder.guest_pass_id}/dismiss-home-card`, {}, { headers });
      setTipReminder(null);
      toast.success('Tip reminder hidden');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to hide this reminder right now');
    } finally {
      setDismissingTipReminder(false);
    }
  }, [headers, tipReminder]);

  const firstName = user?.name?.split(' ')[0] || user?.username || 'Guest';

  return (
    <div className="app-shell" style={{ backgroundColor: 'var(--surface)' }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <PageInfoButton
          onClick={() => setTourOpen(true)}
          testId="guest-home-info-button"
          ariaLabel="Replay Allure tour"
        />
        <NotificationBellButton buttonTestId="guest-home-bell-button" popupTestId="guest-home-notification-popup" />
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 0 }}>
        <RouteTransitionLogo width={140} height={125} dataTestId="guest-home-shared-logo" />

        <motion.h1
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          style={{
            fontFamily: "'Amarante', serif",
            fontSize: 28,
            fontWeight: 400,
            color: 'var(--text)',
            textAlign: 'center',
            marginTop: 8,
            marginBottom: 28,
          }}
        >
          Welcome {firstName}!
        </motion.h1>

        {loading ? (
          <div style={{ width: 222, height: 222, borderRadius: '50%', backgroundColor: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, color: 'var(--text-2)' }}>Loading...</span>
          </div>
        ) : progress ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.25, ease: 'easeOut' }}
          >
            <ProgressCircle
              percent={progress.progress_percent}
              spent={progress.total_spent}
              saved={progress.total_saved}
              savingsBreakdown={progress.recent_savings}
              target={progress.next_threshold}
              size={222}
            />
          </motion.div>
        ) : null}

        {progress && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            style={{ width: '100%', padding: '24px 20px 0', display: 'flex', justifyContent: 'center' }}
          >
            <GuestDiscountSummaryCard discount={progress.discount} />
          </motion.div>
        )}

        {tipReminder ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.5 }}
            style={{ width: '100%', padding: '22px 20px 0', display: 'flex', justifyContent: 'center' }}
          >
            <GuestTipReminderCard
              prompt={tipReminder}
              dismissing={dismissingTipReminder}
              onDismiss={handleDismissTipReminder}
              onOpen={handleOpenTipReminder}
            />
          </motion.div>
        ) : null}

        {ratingPrompt ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: tipReminder ? 0.6 : 0.5 }}
            style={{ width: '100%', padding: '14px 20px 0', display: 'flex', justifyContent: 'center' }}
          >
            <VenueRatingReminderCard
              prompt={ratingPrompt}
              skipping={skippingRating}
              onOpen={handleOpenRating}
              onSkip={handleSkipRating}
            />
          </motion.div>
        ) : null}
      </div>

      <GuestBottomNav />

      <IOSInstallBanner />

      <WelcomeTourSheet
        open={tourOpen}
        onClose={handleCloseTour}
        slides={guestWelcomeTourSlides}
        testIdPrefix="guest-welcome-tour"
      />
    </div>
  );
}
